import http from "@/core/services/http.service";
import _ from 'lodash';

class BucketHelperService {

  convertPathToBlob(path) {
    return fetch(path)
      .then(res => res.blob())
  }

  uploadBlobToBucket(url, blob) {
    return fetch(url, {
      method: 'PUT',
      body: blob,
      headers: {'Content-Type': blob.type},
    })
  }

  markImagesFailed(imageUuids, objectType) {
    const payload = {image_uuids: imageUuids, object_type: objectType}
    return http.post('/api/v1/bucket/mark-images-failed/', payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  uploadImageToBucket(presignedUrl, body) {
    return fetch(presignedUrl, {
      method: 'PUT', body: body
    })
  }


  /**
   * @param images {Array}
   * @param uploadUrl {String}
   * @param objectType {String}
   * @param jsonData {Object}
   * @param progressEvent {Function}
   * @returns {Promise<{success: String, images: Array, body: Array|Object|Null, errorsList: Array|Null,}>}
   */
  createRenameAndDeleteImages(images, uploadUrl, objectType, jsonData, progressEvent) {
    return new Promise(async (next, rej) => {
      const promises = []
      const positionToFileMapper = {}
      let imagePosition = 1;

      for (let i = 1; i <= images.length; i++) {
        let image = images[i - 1];
        if (image.to_be_deleted) {
          continue
        }
        jsonData[`position_${imagePosition}`] = String(imagePosition);
        if (image.uuid === null) {
          jsonData[`image_${imagePosition}`] = image.file.type;
          jsonData[`filename_${imagePosition}`] = image.file.name;
          if (image.blob === undefined) {
            if (image.blobPromise === undefined) {
              // when uploading images to s3, we need to convert files to blob first which takes time
              // to use time efficiently, when user uploads a file in frontend, we asynchronously convert it to blob
              // so converting the image is a promise function
              // we save this promise in `image` object as a `blobPromise` property
              throw new DOMException('You should set blobPromise property which will convert uploaded file to blob or set blob property')
            }
            // important
            const imagePositionCopy = imagePosition
            const promise = image.blobPromise.then(blob => positionToFileMapper[imagePositionCopy] = {
              blob,
              name: image.file.name
            })
            promises.push(promise)
          } else {
            positionToFileMapper[imagePosition] = {blob: image.blob, name: image.file.name}
          }
        } else {
          jsonData[`uuid_${imagePosition}`] = image.uuid
        }
        imagePosition++;
      }

      // noinspection DuplicatedCode
      const handleError = error => {
        rej(error)
        // terminate function
        throw new DOMException()
      }

      try {
        await Promise.all(promises)
      } catch (err) {
        // this error is connected to image conversion to blob so no need for extra error handling
        // default error will be shown to user ('error when uploading images')
        handleError({success: false, images: null, body: err.body})
      }

      let response
      try {
        response = await http.post(uploadUrl, jsonData, {headers: {'Content-Type': 'application/json'}})
      } catch (err) {
        // this is validation error coming from backend
        // error should be handled in caller's side
        handleError({success: false, images: null, body: err.body})
      }

      /**
       * @param response {{
       *   body: {
       *     presigned_urls: [{
       *       image_uuid: String,
       *       position: Number,
       *       presigned_url: String
       *     }],
       *     json_presigned_url: String,
       *     sorted_images: Array,
       *   }
       * }}
       */
      const body = response.body
      const presignedUrls = body.presigned_urls
      // const jsonPresignedUrl = body.json_presigned_url;
      // const renamedImages = body.renamed_images;
      let failedToRenameAndDeleteImages = false;
      const chunkSize = 5;
      let finishedCount = 0;

      let chunkedPresignedUrls = _.chunk(presignedUrls, chunkSize);
      const failedImageUUidToNameMapper = {}

      for (let chunk of chunkedPresignedUrls) {
        let chunkPromises = []
        for (let presignedUrlObj of chunk) {
          const imageUuid = presignedUrlObj.image_uuid;
          const position = presignedUrlObj.position
          const presignedUrl = presignedUrlObj.presigned_url
          const {blob, name} = positionToFileMapper[position];
          chunkPromises.push(new Promise(async (next, rej) => {
            try {
              await this.uploadBlobToBucket(presignedUrl, blob)
              finishedCount++;
              progressEvent(presignedUrls.length, finishedCount);
              next()
            } catch (err) {
              failedImageUUidToNameMapper[imageUuid] = name;
              next()
            }
          }))
        }
        await Promise.all(chunkPromises)
      }

      if (body.renamed_images instanceof Array) {
        for (let item of body.renamed_images) {
          const renameJsonContent = JSON.stringify({
            renamed_images: item.images, deleted_images: {}, object_type: objectType
          });
          try {
            // TODO upload asynchronously
            await this.uploadBlobToBucket(item.url, renameJsonContent, 'application/json')
          } catch (err) {
            failedToRenameAndDeleteImages = true;
          }
        }
      }

      // const jsonContentForRename = JSON.stringify({
      //   renamed_images: renamedImages, deleted_images: {}, object_type: objectType
      // })
      // try {
      //   await this.uploadBlobToBucket(jsonPresignedUrl, jsonContentForRename, 'application/json')
      // } catch (err) {
      //   failedToRenameAndDeleteImages = true
      // }

      for (let item of body.deleted_images) {
        const deletionJsonContent = JSON.stringify({
          renamed_images: {}, deleted_images: item.images, object_type: objectType
        });
        try {
          // TODO upload asynchronously
          await this.uploadBlobToBucket(item.url, deletionJsonContent, 'application/json')
        } catch (err) {
          failedToRenameAndDeleteImages = true;
        }
      }

      const errors = []
      const failedImageUuids = Object.keys(failedImageUUidToNameMapper)
      const failedImageNames = Object.values(failedImageUUidToNameMapper)
      if (failedImageUuids.length > 0) {
        // TODO only one image is marked failed while others are omitted
        // noinspection ES6MissingAwait
        this.markImagesFailed(failedImageUuids, objectType)
        errors.push(`Failed to upload following image(s): ${failedImageNames.join(', ')}`)
      }
      if (failedToRenameAndDeleteImages) {
        errors.push(`Failed to delete/change image positions. Please, try again`)
      }

      if (errors.length > 0) {
        handleError({success: false, images: null, body: null, errorsList: errors})
      }

      return next({success: response.success, images: response.body.sorted_images, body: response.body})
    })
  }
}

const bucketHelperService = new BucketHelperService()
export default bucketHelperService
